import type { PaymentSourceAccountOption } from '../../data/wagepro_store';

/**
 * Pure helpers backing `DisbursementSection`'s "quick-fill" payment-source
 * -account picker (C8, #22330, adversarial-review finding #1) — split out
 * from `post_run_sections.tsx` so they're unit-testable without rendering
 * React, and deliberately NOT added to `post_run_actions_calculations.ts`
 * (that file must stay byte-for-byte unchanged per this issue's own
 * acceptance criteria).
 *
 * ── Why this is a "quick-fill" convenience, not the account field itself ──
 *
 * `payroll.getSalaryDisbursementFile`'s `debit_account_no` is a plain string
 * embedded verbatim into a downloadable bank-upload file as the EMPLOYER'S
 * real funding account number (`services/payroll/salary_disbursement.ts`'s
 * `DisbursementBatch.debitAccountNo`) — it is NOT a chart-of-accounts id.
 * `payroll.listPaymentSourceAccounts` (C5, #22324) only exposes GL account
 * rows (`id`/`name`/`code`/`currency_code`), never a real bank account
 * number. An EARLIER version of this picker replaced the free-text account
 * field outright with a `<select>` populated from this list, submitting
 * `account.code ?? account.id` as `debit_account_no` — an adversarial review
 * pass (C8) correctly flagged this as unsafe: a `<select>` cannot be
 * "corrected" by the user the way a text field can, so a GL code that
 * doesn't match the entity's real bank account number would go straight
 * into the downloaded file with no way to fix it in the UI. The free-text
 * `Input` for the actual account number is therefore RESTORED as the
 * primary, always-functional control (unaffected by whether
 * `listPaymentSourceAccounts` resolves, is empty, or fails); this picker
 * only offers a one-click "fill the account-number field from one of our
 * configured accounts" convenience the user can still freely overwrite
 * afterwards, same as if they'd typed it themselves.
 */

/**
 * The two fields an account's picker label reads. Structural so every
 * bank/cash account row shape in this app fits without an adapter —
 * `PaymentSourceAccountOption` and `BankAccountOption` (`wagepro_store.tsx`),
 * `RealPaymentSourceAccountRow` (`oauth_pay_run_post_actions.ts`).
 */
export interface BankAccountLike {
  code: string | null;
  name: string;
}

/**
 * The picker option label — `"<code> · <name>"` when a code exists, else
 * just the name. The ONE owner of this label (#24385): the disbursement
 * quick-fill list, the statutory "Paid from" picker, the LK final-pay
 * drawer (`post_run_select_options.ts`'s `build_bank_account_options`) and
 * Data Centre's Bank tab (`data_centre_bank_section.tsx`) all render it
 * through here, so they cannot drift apart.
 */
export function formatPaymentSourceAccountLabel(
  account: BankAccountLike,
): string {
  return account.code ? `${account.code} · ${account.name}` : account.name;
}

/**
 * The value quick-filled into the disbursement dialog's `debit_account_no`
 * field when this account is picked — the GL `code` when set, else the
 * account's own `id` as a last resort. Neither is a verified real bank
 * account number (see module docblock) — the field stays editable so the
 * user can correct it.
 */
export function resolvePaymentSourceQuickFillValue(
  account: PaymentSourceAccountOption,
): string {
  // `||`, not `??` -- `accounts.code` is a nullable TEXT column with NO
  // non-empty constraint (`apps/api/src/db/schema/tenant/accounts.ts`), so a
  // real row can have `code: ''` (empty string, not null). `??` only falls
  // back on null/undefined, so an empty-string code would pass through
  // UNCHANGED and collide with this `<select>`'s own `<option value="">`
  // placeholder -- and `findPaymentSourceAccountByQuickFillValue` below
  // explicitly treats `value === ''` as "no selection", so that account
  // becomes silently unselectable with no error shown (adversarial-review
  // finding, C8 second round, #22410). `||` treats `''` the same as
  // null/undefined (falsy), matching `formatPaymentSourceAccountLabel`'s
  // truthiness check above -- both functions now agree on how an
  // empty-string code is handled, instead of one using truthiness and the
  // other nullish-coalescing.
  return account.code || account.id;
}

/** Looks up an account by its quick-fill value (the `<select>`'s `onChange` target), for wiring the picker's selection back to `setAccountNo`. `undefined` for the placeholder option (`''`) or an unrecognised value. */
export function findPaymentSourceAccountByQuickFillValue(
  accounts: readonly PaymentSourceAccountOption[],
  value: string,
): PaymentSourceAccountOption | undefined {
  if (value === '') return undefined;
  return accounts.find(
    (account) => resolvePaymentSourceQuickFillValue(account) === value,
  );
}
